import type { ReactNode } from 'react';
import { Modal as HeroModal } from '@heroui/react/modal';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { cn } from '@/utils/cn';
import { useSheetSwipe } from './use-sheet-swipe';

/**
 * The app's only dialog.
 *
 * Collapses HeroUI's compound API into one declarative component so every
 * modal shares structure, spacing and footer button order (secondary
 * "Cancel" then primary/danger confirm).
 *
 * Mobile renders as a bottom sheet, desktop as a centred card — handled here
 * once rather than per-modal. `placement="auto"` is what does it: HeroUI maps
 * it to `mt-auto` (docked to the bottom) and `sm:my-auto` (centred), plus the
 * slide-up entrance on mobile.
 */
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

/**
 * Width per size. Applied to the *dialog*, not the container.
 *
 * HeroUI's `.modal__container` is `sm:w-fit`, so a max-width on the container
 * cannot widen it — the dialog collapses to its content width. Its own `size`
 * variant also caps the dialog at `max-w-md` (448px) by default, which
 * squeezed every form modal in the app. `ds.css` drops that cap so these
 * widths win; `sm:` keeps mobile as a full-width bottom sheet.
 */
const SIZE_CLASS: Record<ModalSize, string> = {
  sm: 'sm:w-[24rem]',
  md: 'sm:w-[28rem]',
  lg: 'sm:w-[32rem]',
  xl: 'sm:w-[36rem]',
  '2xl': 'sm:w-[42rem]',
  '3xl': 'sm:w-[48rem]',
  '4xl': 'sm:w-[56rem]',
};

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Secondary line under the title. */
  subtitle?: ReactNode;
  /** Alias for `subtitle`. */
  description?: ReactNode;
  /** Icon shown in a tinted square beside the title. */
  icon?: ReactNode;
  children: ReactNode;
  size?: ModalSize;
  /** Alias for `size`, matching the pre-migration Modal's prop name. */
  maxWidth?: ModalSize;
  /** Custom footer. Omit and pass `onConfirm` for the standard footer. */
  footer?: ReactNode;
  confirmLabel?: string;
  onConfirm?: () => void;
  /** Renders the confirm action as destructive. */
  isDestructive?: boolean;
  isConfirmLoading?: boolean;
  isConfirmDisabled?: boolean;
  cancelLabel?: string;
  /** Blocks backdrop/Esc dismissal — for unsaved-changes flows. */
  isDismissable?: boolean;
  /** Removes body padding when the content manages its own. */
  noPadding?: boolean;
  className?: string;
  bodyClassName?: string;
  headerClassName?: string;
  /** Anchor for a guided tour step (see `components/tour`). */
  'data-tour'?: string;
}

/**
 * A guided tour can walk through an open dialog. Its popover lives outside
 * the dialog, so without this a press on its Next button counts as a click
 * outside and closes the dialog the tour is explaining.
 */
const isOutsideTheTour = (element: Element) =>
  !element.closest('.driver-popover');

export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  description,
  icon,
  children,
  size,
  maxWidth,
  footer,
  confirmLabel,
  onConfirm,
  isDestructive = false,
  isConfirmLoading = false,
  isConfirmDisabled = false,
  cancelLabel,
  isDismissable = true,
  noPadding = false,
  className,
  bodyClassName,
  headerClassName,
  'data-tour': tourAnchor,
}: ModalProps) {
  const { t } = useTranslation();
  // On a phone the dialog is a bottom sheet; swiping it down closes it,
  // unless the caller blocked dismissal (an unsaved-changes flow).
  const sheetHandleRef = useSheetSwipe(isDismissable, onClose);

  const standardFooter = onConfirm && (
    <>
      <Button
        variant="secondary"
        onPress={onClose}
        isDisabled={isConfirmLoading}
      >
        {cancelLabel ?? t('common.cancel', 'Cancel')}
      </Button>
      <Button
        variant={isDestructive ? 'danger' : 'primary'}
        onPress={onConfirm}
        isLoading={isConfirmLoading}
        isDisabled={isConfirmDisabled}
      >
        {confirmLabel ?? t('common.confirm', 'Confirm')}
      </Button>
    </>
  );

  const resolvedFooter = footer ?? standardFooter;

  return (
    <HeroModal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <HeroModal.Backdrop
        isDismissable={isDismissable}
        shouldCloseOnInteractOutside={isOutsideTheTour}
        className="ds-modal-backdrop"
      >
        {/*
          `scroll="inside"` keeps the dialog within the viewport and scrolls
          its BODY, leaving the header and footer pinned. Without it HeroUI
          leaves the body at `overflow: visible`, so a tall modal simply ran
          off the bottom of a phone screen with no way to reach the rest of
          the form or the footer buttons.
        */}
        <HeroModal.Container
          placement="auto"
          scroll="inside"
          className="ds-modal"
        >
          <HeroModal.Dialog
            data-tour={tourAnchor}
            className={cn(SIZE_CLASS[size ?? maxWidth ?? 'md'], className)}
          >
            {/* Grab handle, phones only (ds.css). */}
            <div ref={sheetHandleRef} aria-hidden className="ds-sheet-handle" />
            <HeroModal.Header
              className={cn('flex items-start gap-3', headerClassName)}
            >
              {icon && (
                <div
                  className="mt-0.5 flex size-9 shrink-0 items-center justify-center
                    rounded-md bg-primary-50 text-primary-700 [&_svg]:size-4"
                >
                  {icon}
                </div>
              )}
              <div className="min-w-0 space-y-1">
                <HeroModal.Heading className="text-h2 text-foreground">
                  {title}
                </HeroModal.Heading>
                {(subtitle ?? description) && (
                  <p className="text-small text-muted">
                    {subtitle ?? description}
                  </p>
                )}
              </div>
              <HeroModal.CloseTrigger className="ms-auto" />
            </HeroModal.Header>

            <HeroModal.Body
              className={cn(!noPadding && 'space-y-6', bodyClassName)}
            >
              {children}
            </HeroModal.Body>

            {resolvedFooter && (
              <HeroModal.Footer className="flex items-center justify-end gap-3">
                {resolvedFooter}
              </HeroModal.Footer>
            )}
          </HeroModal.Dialog>
        </HeroModal.Container>
      </HeroModal.Backdrop>
    </HeroModal>
  );
}

/** Destructive confirmation. Thin preset over Modal so it cannot drift. */
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  isLoading,
  isDestructive = true,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  isDestructive?: boolean;
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title={title}
      size="sm"
      isDestructive={isDestructive}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      isConfirmLoading={isLoading}
    >
      <p className="text-body text-muted">{message}</p>
    </Modal>
  );
}
